import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { LEAD_TEL_HREF, LEAD_WHATSAPP_URL_HERO } from '../constants/leadContact';
import './FloatingLeadCTA.css';

const WA_HREF = LEAD_WHATSAPP_URL_HERO;
const TEL_HREF = LEAD_TEL_HREF;

const SCROLL_REVEAL_RATIO = 0.25;
const SHORT_PAGE_MAX_SCROLL = 80;
const SHORT_PAGE_DELAY_MS = 2500;

function isHiddenRoute(pathname) {
  return (
    pathname.startsWith('/admin') ||
    pathname.startsWith('/employee')
  );
}

function getScrollMetrics() {
  const el = document.documentElement;
  const scrollTop = el.scrollTop || document.body.scrollTop;
  const maxScroll = el.scrollHeight - el.clientHeight;
  return { scrollTop, maxScroll };
}

function WhatsAppIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  );
}

export default function FloatingLeadCTA() {
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);
  const shortTimerRef = useRef(null);

  const evaluateScroll = useCallback(() => {
    const { scrollTop, maxScroll } = getScrollMetrics();
    if (maxScroll < SHORT_PAGE_MAX_SCROLL) {
      return;
    }
    const ratio = maxScroll > 0 ? scrollTop / maxScroll : 1;
    if (ratio >= SCROLL_REVEAL_RATIO) {
      setVisible(true);
    }
  }, []);

  useEffect(() => {
    if (isHiddenRoute(pathname)) {
      setVisible(false);
      return undefined;
    }

    setVisible(false);
    const { maxScroll } = getScrollMetrics();

    if (maxScroll < SHORT_PAGE_MAX_SCROLL) {
      shortTimerRef.current = window.setTimeout(() => {
        setVisible(true);
      }, SHORT_PAGE_DELAY_MS);
    }

    const onScroll = () => {
      evaluateScroll();
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    evaluateScroll();

    return () => {
      window.removeEventListener('scroll', onScroll);
      if (shortTimerRef.current) {
        window.clearTimeout(shortTimerRef.current);
        shortTimerRef.current = null;
      }
    };
  }, [pathname, evaluateScroll]);

  if (isHiddenRoute(pathname)) {
    return null;
  }

  return (
    <div
      className={`floating-lead-cta-wrap${visible ? ' is-visible' : ''}`}
      aria-hidden={!visible}
    >
      <a
        href={WA_HREF}
        target="_blank"
        rel="noopener noreferrer"
        className="floating-lead-cta floating-lead-cta--wa is-pulse"
        aria-label="Chat on WhatsApp"
        data-tooltip="Chat with us"
        title="Chat with us"
        onClick={addRipple}
      >
        <WhatsAppIcon />
      </a>
      <a
        href={TEL_HREF}
        className="floating-lead-cta floating-lead-cta--call"
        aria-label="Call us"
        data-tooltip="Call now"
        title="Call now"
        onClick={addRipple}
      >
        <PhoneIcon />
      </a>
    </div>
  );
}

function addRipple(e) {
  if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return;
  }
  const btn = e.currentTarget;
  const existing = btn.querySelector('.floating-lead-cta-ripple');
  if (existing) existing.remove();

  const r = document.createElement('span');
  r.className = 'floating-lead-cta-ripple';
  const size = Math.max(btn.offsetWidth, btn.offsetHeight);
  r.style.width = r.style.height = `${size}px`;
  r.style.left = `${e.clientX - btn.getBoundingClientRect().left - size / 2}px`;
  r.style.top = `${e.clientY - btn.getBoundingClientRect().top - size / 2}px`;
  btn.appendChild(r);
  r.addEventListener('animationend', () => r.remove(), { once: true });
}
